import { useTranslation } from 'react-i18next';
import { useGetOrderById } from '~/api';
import { assertDefined, calculateOrderTotal, formatDateTime } from '~/utils';
import { BackButton, ClipSpinner } from '~/components/ui';
import { ItemCard } from '~/components/cards/ItemCard.tsx';
import { OrderProgressTrackerCard } from '~/components/cards/OrderProgessTrackerCard.tsx';
import { OrderStatusBadge } from '~/components/ui/OrderStatusBadge';
import type { OrderStatus } from '~/types/orders.ts';
import { SellerOrderCallToAction } from '~/pages/order-view/sections/SellerCallToAction';
import { SellerShippingCard } from '~/pages/order-view/sections/SellerShippingCard';
import { SellerOrderSummaryCard } from '~/pages/order-view/sections/SellerOrderSummaryCard';
import { BuyingInformationCard } from '~/pages/order-view/sections/BuyingInformationCard';
import { getGradeInformation } from '~/utils/order';

export function SellerOrderView({ orderId }: Readonly<{ orderId: string }>) {
  const { t } = useTranslation('order');

  const { data, isLoading, isError, error } = useGetOrderById(orderId);

  if (isLoading) return <ClipSpinner />;
  if (isError)
    return <div>{t('order.loading_error', { message: error.message })}</div>;

  assertDefined(data?.order, 'Order data should be defined');
  const order = data.order;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      <BackButton link="/orders" label="Back to Orders" />
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <h1 className="text-2xl text-neut-prim font-semibold">
            {t('order.seller_order_id', { orderId: order.id })}
          </h1>
          <p className="text-sm text-gray-600">
            {t('order.seller_order_placed_on', {
              orderDate: formatDateTime(order.createdAt),
            })}
          </p>
        </div>
        <OrderStatusBadge status={order.status} size="small" />
      </div>
      <div className="bg-neut-bg-alt p-6 rounded-lg flex flex-col gap-4 border border-neut-200 shadow-sm">
        <OrderProgressTrackerCard order={order} />
        <SellerOrderCallToAction
          orderId={order.id}
          orderStatus={order.status as OrderStatus}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-neut-prim font-semibold">
                {t('order.order_items')}
              </h2>
            </div>
            {order.items.map((item) => (
              <ItemCard
                key={item.platformProductsLengths?.id}
                productName={
                  item.platformProductsLengths?.platformProduct.name ||
                  'not found'
                }
                itemQuantity={item.quantity.value}
                unit={item.quantity.unit}
                pricePerUnit={item.price}
                length={
                  item.platformProductsLengths?.length.displayValue || 'N/A'
                }
                gradeInformation={getGradeInformation(
                  item.platformProductsLengths ?? { platformProduct: {} }
                )}
              />
            ))}
          </div>
          <SellerShippingCard
            contact={order.buyer.firstName}
            shippingLocationName={order.shippingLocation.name}
            addressLine1={order.shippingLocation.addressLine1}
            addressLine2={order.shippingLocation.addressLine2}
            city={order.shippingLocation.city}
            region={order.shippingLocation.region}
            zipCode={order.shippingLocation.zipCode}
          />
        </div>
        <div className="lg:col-span-1 space-y-6">
          <BuyingInformationCard
            buyerName={order.buyer.firstName}
            companyName={order.shippingLocation.organization.name}
          />
          <SellerOrderSummaryCard orderTotal={calculateOrderTotal(order)} />
        </div>
      </div>
    </div>
  );
}
